/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ClarityManager } from '../components/ClarityManager'
import { ContestPromoPopup } from '../components/ContestPromoPopup'
import { CookieConsentBanner } from '../components/CookieConsentBanner'
import { ComparePage } from '../pages/ComparePage'
import { DiscoverPage } from '../pages/DiscoverPage'
import { LandingPage } from '../pages/LandingPage'
import { ScrollManager } from './ScrollManager'
import { RequireAuth } from './RequireAuth'
import { RequireFullPanelAccess } from './RequireFullPanelAccess'
import { RequireAdmin } from './RequireAdmin'

const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const AboutProjectPage = lazy(() =>
  import('../pages/AboutProjectPage').then((m) => ({ default: m.AboutProjectPage })),
)
const NewsPage = lazy(() => import('../pages/NewsPage').then((m) => ({ default: m.NewsPage })))
const NewsArticlePage = lazy(() =>
  import('../pages/NewsArticlePage').then((m) => ({ default: m.NewsArticlePage })),
)
const RankingPage = lazy(() =>
  import('../pages/RankingPage').then((m) => ({ default: m.RankingPage })),
)
const LoginPage = lazy(() =>
  import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const ResetPasswordPage = lazy(() =>
  import('../pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const FormularzPage = lazy(() =>
  import('../pages/FormularzPage').then((m) => ({ default: m.FormularzPage })),
)
const RedeemPage = lazy(() =>
  import('../pages/RedeemPage').then((m) => ({ default: m.RedeemPage })),
)
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)
const PlacePublicDetailsPage = lazy(() =>
  import('../pages/PlacePublicDetailsPage').then((m) => ({
    default: m.PlacePublicDetailsPage,
  })),
)
const PlacesPage = lazy(() =>
  import('../pages/PlacesPage').then((m) => ({
    default: m.PlacesPage,
  })),
)
const PrivacyPolicyPage = lazy(() =>
  import('../pages/PrivacyPolicyPage').then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
)
const AccountDeletionPage = lazy(() =>
  import('../pages/AccountDeletionPage').then((m) => ({
    default: m.AccountDeletionPage,
  })),
)
const SocialChannelRulesPage = lazy(() =>
  import('../pages/SocialChannelRulesPage').then((m) => ({
    default: m.SocialChannelRulesPage,
  })),
)
const TermsOfServicePage = lazy(() =>
  import('../pages/TermsOfServicePage').then((m) => ({
    default: m.TermsOfServicePage,
  })),
)
const Promocja25Page = lazy(() =>
  import('../pages/Promocja25Page').then((m) => ({
    default: m.Promocja25Page,
  })),
)
const NewsletterLoginPage = lazy(() =>
  import('../pages/NewsletterLoginPage').then((m) => ({
    default: m.NewsletterLoginPage,
  })),
)
const CommunityPage = lazy(() =>
  import('../pages/CommunityPage').then((m) => ({
    default: m.CommunityPage,
  })),
)
const PanelCommunityPage = lazy(() =>
  import('../pages/panel/PanelCommunityPage').then((m) => ({
    default: m.PanelCommunityPage,
  })),
)
const PanelJournalPage = lazy(() =>
  import('../pages/panel/PanelJournalPage').then((m) => ({
    default: m.PanelJournalPage,
  })),
)
const PanelPlaceDetailsPage = lazy(() =>
  import('../pages/panel/PanelPlaceDetailsPage').then((m) => ({
    default: m.PanelPlaceDetailsPage,
  })),
)
const PanelPlacesListPage = lazy(() =>
  import('../pages/panel/PanelPlacesListPage').then((m) => ({
    default: m.PanelPlacesListPage,
  })),
)
const PanelSettingsPage = lazy(() =>
  import('../pages/panel/PanelSettingsPage').then((m) => ({
    default: m.PanelSettingsPage,
  })),
)
const PanelWelcomePage = lazy(() =>
  import('../pages/panel/PanelWelcomePage').then((m) => ({
    default: m.PanelWelcomePage,
  })),
)
const PanelQuizPage = lazy(() =>
  import('../pages/panel/PanelQuizPage').then((m) => ({
    default: m.PanelQuizPage,
  })),
)
const AdminDashboardPage = lazy(() =>
  import('../pages/admin/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
)
const AdminUsersPage = lazy(() =>
  import('../pages/admin/AdminUsersPage').then((m) => ({
    default: m.AdminUsersPage,
  })),
)
const AdminPaymentsPage = lazy(() =>
  import('../pages/admin/AdminPaymentsPage').then((m) => ({
    default: m.AdminPaymentsPage,
  })),
)
const AdminCommentsPage = lazy(() =>
  import('../pages/admin/AdminCommentsPage').then((m) => ({
    default: m.AdminCommentsPage,
  })),
)
const AdminCommunityPage = lazy(() =>
  import('../pages/admin/AdminCommunityPage').then((m) => ({
    default: m.AdminCommunityPage,
  })),
)
const AdminWelcomePage = lazy(() =>
  import('../pages/admin/AdminWelcomePage').then((m) => ({
    default: m.AdminWelcomePage,
  })),
)
const AdminNoAccessPage = lazy(() =>
  import('../pages/admin/AdminNoAccessPage').then((m) => ({
    default: m.AdminNoAccessPage,
  })),
)
const AdminWaitlistPage = lazy(() =>
  import('../pages/admin/AdminWaitlistPage').then((m) => ({
    default: m.AdminWaitlistPage,
  })),
)
const AdminStatsPage = lazy(() =>
  import('../pages/admin/AdminStatsPage').then((m) => ({
    default: m.AdminStatsPage,
  })),
)
const AdminTrialAccountsPage = lazy(() =>
  import('../pages/admin/AdminTrialAccountsPage').then((m) => ({
    default: m.AdminTrialAccountsPage,
  })),
)
const AdminFreeAccountsPage = lazy(() =>
  import('../pages/admin/AdminFreeAccountsPage').then((m) => ({
    default: m.AdminFreeAccountsPage,
  })),
)
const AdminHomeEventsPage = lazy(() =>
  import('../pages/admin/AdminHomeEventsPage').then((m) => ({
    default: m.AdminHomeEventsPage,
  })),
)
const LinkBioPage = lazy(() =>
  import('../pages/LinkBioPage').then((m) => ({
    default: m.LinkBioPage,
  })),
)

function RootLayout() {
  const { pathname } = useLocation()
  const isLinkBio = pathname === '/link-bio'

  return (
    <>
      <ClarityManager />
      <ScrollManager />
      <Outlet />
      {!isLinkBio && <CookieConsentBanner />}
      {!isLinkBio && <ContestPromoPopup />}
    </>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/discover', element: <DiscoverPage /> },
      { path: '/porownanie', element: <ComparePage /> },
      { path: '/o-projekcie', element: <AboutProjectPage /> },
      { path: '/o-projekcie/aktualnosci', element: <NewsPage /> },
      { path: '/o-projekcie/aktualnosci/:slug', element: <NewsArticlePage /> },
      { path: '/o-projekcie/ranking', element: <RankingPage /> },
      { path: '/cennik', element: <Navigate to="/formularz" replace /> },
      { path: '/promocja', element: <Navigate to="/formularz" replace /> },
      { path: '/promocja-25', element: <Promocja25Page /> },
      { path: '/newsletter-login', element: <NewsletterLoginPage /> },
      { path: '/regulamin', element: <TermsOfServicePage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/formularz', element: <FormularzPage /> },
      { path: '/redeem', element: <RedeemPage /> },
      { path: '/miejsca', element: <PlacesPage /> },
      { path: '/miejsca/:placeId', element: <PlacePublicDetailsPage /> },
      { path: '/regulamin', element: <TermsOfServicePage /> },
      { path: '/polityka-prywatnosci', element: <PrivacyPolicyPage /> },
      { path: '/usun-konto', element: <AccountDeletionPage /> },
      { path: '/regulamin-kanalu-spolecznosciowego', element: <SocialChannelRulesPage /> },
      { path: '/reset-hasla', element: <ResetPasswordPage /> },
      { path: '/spolecznosc', element: <CommunityPage /> },
      { path: '/link-bio', element: <LinkBioPage /> },
      {
        path: '/panel',
        element: <DashboardPage />,
        children: [
          {
            index: true,
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelWelcomePage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'dziennik',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelJournalPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'miejsca',
            element: (
              <RequireAuth>
                <PanelPlacesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'edycja-2026',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelPlacesListPage catalogTier="mini" />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'edycja-2026/:placeId',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelPlaceDetailsPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'miejsca/:placeId',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelPlaceDetailsPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'spolecznosc',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelCommunityPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'quiz',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelQuizPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
          {
            path: 'ustawienia',
            element: (
              <RequireAuth>
                <RequireFullPanelAccess>
                  <PanelSettingsPage />
                </RequireFullPanelAccess>
              </RequireAuth>
            ),
          },
        ],
      },
      {
        path: '/admin',
        element: (
          <RequireAuth>
            <RequireAdmin>
              <AdminDashboardPage />
            </RequireAdmin>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <AdminWelcomePage /> },
          { path: 'uzytkownicy', element: <AdminUsersPage /> },
          { path: 'darmowe-konta', element: <AdminFreeAccountsPage /> },
          { path: 'konta-testowe', element: <AdminTrialAccountsPage /> },
          { path: 'wplaty', element: <AdminPaymentsPage /> },
          { path: 'komentarze', element: <AdminCommentsPage /> },
          { path: 'spolecznosc', element: <AdminCommunityPage /> },
          { path: 'wydarzenia', element: <AdminHomeEventsPage /> },
          { path: 'waitlista', element: <AdminWaitlistPage /> },
          { path: 'statystyki', element: <AdminStatsPage /> },
        ],
      },
      {
        path: '/admin/brak-dostepu',
        element: (
          <RequireAuth>
            <AdminNoAccessPage />
          </RequireAuth>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
